<template>
  <div
    class="mission-card-hexagon"
    :class="[
      `is-${achievementStatus}`,
      `color-type-${mission.colorType}`,
      `condition-type-${mission.honeycombType}`,
      { 'is-all-completed': mission.hasCompleted },
    ]"
  >
    <div class="mission-card-hexagon__outer">
      <mission-hexagon-outer-icon />
    </div>
    <div class="mission-card-hexagon__inner" v-if="achievementStatus === 'completed'">
      <mission-hexagon-inner-icon />
    </div>
    <p
      class="mission-card-hexagon__count"
      v-if="mission.honeycombType === 'count' || mission.honeycombType === 'countNoOrder'"
    >
      {{ condition.operationCount }}
    </p>
    <p class="mission-card-hexagon__check" v-if="mission.honeycombType === 'check'">
      <mission-check-icon
        :width="iconSize.x"
        :height="iconSize.y"
        class="mission-card-hexagon__check-icon"
      />
    </p>
    <p class="mission-card-hexagon__point" v-if="achievementStatus !== 'completed'">
      +{{ condition.point }}P
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref } from '@vue/composition-api'
import DeviceInfo from '@/util/DeviceInfo'
import MissionHexagonOuterIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionHexagonOuterIcon.vue'
import MissionHexagonInnerIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionHexagonInnerIcon.vue'
import MissionCheckIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionCheckIcon.vue'
import { MissionProgressCardType } from '@/components/MissionPage/hook/useMissionProgressData'
import MissionProgressDocument, {
  AchievementConditionType,
} from '@/store/stores/collectionModule/documents/mission/MissionProgressDocument'
import { ROUND_CHECK_IN_MISSION_CODE_LIST } from '@/util/Const'

/**
 * 1度もミッションを達成したことがない場合に前回の達成条件にセットされる値の型
 */
type NoPreviousConditionType = 'noPreviousCondition'

/**
 * ミッション達成状況を表現する型
 */
type AchievementStatusType = 'inactive' | 'active' | 'completed'

/**
 * ミッション画面 ミッション達成状況 ハニカム表示六角形パーツ
 */
export default defineComponent({
  name: 'MissionHexagonParts',
  components: { MissionCheckIcon, MissionHexagonInnerIcon, MissionHexagonOuterIcon },
  props: {
    /**
     * 達成条件
     */
    condition: {
      type: Object as PropType<AchievementConditionType>,
      required: true,
    },
    /**
     * 前回の達成条件
     */
    prevCondition: {
      type: [Object, String] as PropType<AchievementConditionType | NoPreviousConditionType>,
    },
    /**
     * ミッション達成状況カード
     */
    mission: {
      type: Object as PropType<MissionProgressCardType>,
      required: true,
    },
    /**
     * 開催中のラウンドチェックインまたはイベントチェックインミッション一覧
     */
    checkInMissionsBeingHeld: {
      type: Array as PropType<MissionProgressDocument[]>,
      default: () => [],
    },
  },
  setup(props) {
    const forPc = DeviceInfo.isForPC()

    const iconSize = computed(() => {
      if (props.mission.hasCompleted && forPc) {
        return { x: 74, y: 74 }
      }

      if (props.mission.hasCompleted) {
        return { x: 54, y: 54 }
      }

      if (forPc) {
        return { x: 33, y: 33 }
      }

      return { x: 24, y: 24 }
    })

    /**
     * active状態の取得する
     */
    const getActiveStatus = () => {
      if (props.mission.honeycombType === 'check') {
        // ハニカム内がチェックマークの場合は、activeにする
        return true
      }

      if (props.mission.honeycombType === 'countNoOrder') {
        if (props.mission.missionGroupCode === 'SEASON_ROUND_CHECK_IN') {
          // 開催中のシーズンラウンドチェックインミッションの場合、active状態にする
          return props.checkInMissionsBeingHeld.some((beingHeldMission) => {
            const isRoundCheckInMission = (
              ROUND_CHECK_IN_MISSION_CODE_LIST as Array<string>
            ).includes(beingHeldMission.missionCode || '')
            const round = beingHeldMission.missionCode?.replace('SEASON_ROUND_CHECK_IN_', '')
            return isRoundCheckInMission && round === String(props.condition.operationCount)
          })
        }

        // ミッション達成の順番が順不同な場合は全て非アクティブにする
        return false
      }

      if (props.prevCondition === 'noPreviousCondition') {
        // 該当ミッションを1度も達成していない場合は、最初のミッションをactiveにする
        return true
      }
      // 該当ミッションの一つ前の回数を達成している場合はactiveにする
      return props.prevCondition?.isAchieved
    }

    /**
     * ミッション達成状況のステータスを返す
     */
    const achievementStatus: Ref<AchievementStatusType> = computed(() => {
      if (props.condition.isAchieved) {
        // ハニカム内の該当回数達成済みの場合
        return 'completed'
      }
      if (getActiveStatus()) {
        return 'active'
      }

      // 上記以外
      return 'inactive'
    })

    return {
      iconSize,
      achievementStatus,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-card-hexagon {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(#{convertPXtoVW(66)}, 66px);
  height: min(#{convertPXtoVW(74)}, 74px);

  &.color-type-1 {
    color: #73ffdd;
  }

  &.color-type-2 {
    color: #e0ffa0;
  }

  &.color-type-3 {
    color: #f7c6ff;
  }

  &.color-type-4 {
    color: #ff5d5d;
  }

  &__outer {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(#{convertPXtoVW(66)}, 66px);
    height: min(#{convertPXtoVW(74)}, 74px);
    opacity: 0.2;
    transform: translate(-50%, -50%);
  }

  &__inner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(#{convertPXtoVW(56)}, 56px);
    height: min(#{convertPXtoVW(62)}, 62px);
    opacity: 0.4;
    transform: translate(-50%, -50%);
  }

  &__count {
    font-family: $fontTelemetrySpeedGear;
    font-size: min(#{convertPXtoVW(18)}, 18px);
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  &__check-icon {
    width: 24px;
    height: 24px;
  }

  &__point {
    font-size: min(#{convertPXtoVW(10)}, 10px);
    font-weight: 500;

    .mission-card-hexagon__check + & {
      opacity: 0.3;
    }
  }

  &.is-active {
    .mission-card-hexagon__check {
      opacity: 0.3;

      + .mission-card-hexagon__point {
        opacity: 0.3;
      }
    }

    .mission-card-hexagon__count,
    .mission-card-hexagon__point {
      opacity: 1;
    }
  }

  &.is-inactive {
    .mission-card-hexagon__count,
    .mission-card-hexagon__point {
      opacity: 0.3;
    }
  }

  &.is-completed {
    .mission-card-hexagon__outer {
      opacity: 0.6;
    }
  }

  &.is-all-completed {
    width: min(#{convertPXtoVW(51)}, 51px);
    height: min(#{convertPXtoVW(57)}, 57px);

    &.color-type-1 {
      color: #b8f9e9;
    }

    .mission-card-hexagon__count {
      font-size: min(#{convertPXtoVW(14)}, 14px);
    }

    .mission-card-hexagon__point {
      font-size: min(#{convertPXtoVW(8)}, 8px);
    }

    .mission-card-hexagon__outer {
      width: min(#{convertPXtoVW(51)}, 51px);
      height: min(#{convertPXtoVW(57)}, 57px);
    }

    .mission-card-hexagon__inner {
      width: min(#{convertPXtoVW(45)}, 45px);
      height: min(#{convertPXtoVW(49)}, 49px);
    }

    &.condition-type-check {
      width: min(#{convertPXtoVW(84)}, 84px);
      height: min(#{convertPXtoVW(96)}, 96px);

      .mission-card-hexagon__outer {
        width: min(#{convertPXtoVW(84)}, 84px);
        height: min(#{convertPXtoVW(96)}, 96px);
      }

      .mission-card-hexagon__inner {
        width: min(#{convertPXtoVW(74)}, 74px);
        height: min(#{convertPXtoVW(83)}, 83px);
      }

      .mission-card-hexagon__check-icon {
        width: min(#{convertPXtoVW(54)}, 54px);
        height: min(#{convertPXtoVW(54)}, 54px);
      }
    }
  }

  @media screen and (min-width: $responsiveTabletWidth) {
    width: min(#{convertPXtoVW(90)}, 90px);
    height: min(#{convertPXtoVW(101)}, 101px);

    &__outer {
      width: min(#{convertPXtoVW(90)}, 90px);
      height: min(#{convertPXtoVW(101)}, 101px);
    }

    &__inner {
      width: min(#{convertPXtoVW(78)}, 78px);
      height: min(#{convertPXtoVW(85)}, 85px);
    }

    &__count {
      font-size: min(#{convertPXtoVW(24)}, 24px);
    }

    &__check-icon {
      width: min(#{convertPXtoVW(33)}, 33px);
      height: min(#{convertPXtoVW(33)}, 33px);
    }

    &__point {
      font-size: min(#{convertPXtoVW(14)}, 14px);
    }

    &.is-all-completed {
      width: 70px;
      height: 78px;

      .mission-card-hexagon__count {
        font-size: 19px;
      }

      .mission-card-hexagon__point {
        font-size: 11px;
      }

      .mission-card-hexagon__outer {
        width: 70px;
        height: 78px;
      }

      .mission-card-hexagon__inner {
        width: 62px;
        height: 67px;
      }

      &.condition-type-check {
        width: 115px;
        height: 131px;

        .mission-card-hexagon__outer {
          width: 115px;
          height: 131px;
        }

        .mission-card-hexagon__inner {
          width: 101px;
          height: 114px;
        }

        .mission-card-hexagon__check-icon {
          width: 74px;
          height: 74px;
        }
      }
    }
  }
}
</style>
